import { inject, Injectable } from '@angular/core';
import { PermissionCode, TRACKER_PERMISSION_CODE, TrackerArea } from '../interfaces/lookup.interface';
import { FacilityService } from './facility.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class AccessService {

  private readonly userService = inject(UserService);

  private readonly facilityService = inject(FacilityService);
  
  canReadTracker(area: TrackerArea): boolean {
    if (!this.userService.profileLoaded()) return true;

    // Scoped to the selected facility, so the menu follows the selector. Reading selectedId here
    // is what makes NavigationService.navItems recompute when the user switches facility.
    const facilityId = this.facilityService.selectedId();
    if (facilityId === null) return true;

    return this.userService.facilityPermissions().some(permission =>
      permission.facilityId === facilityId
      && permission.permissionCode === TRACKER_PERMISSION_CODE[area]
      && permission.isRead);
  }

  // Whether the user holds read access to a permission at *any* of their facilities.
  //
  // The facility-agnostic counterpart to canReadTracker, and the right gate for Reports and
  // Administration: those pages carry no facility selector - `showFacility` is set on the two
  // Tracker items only - so scoping them to a selection the user cannot see or change from those
  // pages would make the menu change for reasons they have no way to understand.
  //
  // Permissive until the profile resolves, for the same reason canReadTracker is: navItems$ waits
  // only on the Entra groups, so hiding an item on an unresolved grid would have
  // groupPermissionGuard bounce the user off their landing page.
  canReadAnyFacility(permissionCode: PermissionCode): boolean {
    if (!this.userService.profileLoaded()) return true;

    return this.userService.facilityPermissions().some(permission =>
      permission.permissionCode === permissionCode && permission.isRead);
  }

  canEdit(area: TrackerArea, facilityKey?: string | null): boolean {
    const facilityId = facilityKey
      ? this.facilityService.facilities().find(facility => facility.facilityKey === facilityKey)?.id ?? null
      : this.facilityService.selectedId();

    if (facilityId === null) return false;

    return this.userService.facilityPermissions().some(permission =>
      permission.facilityId === facilityId
      && permission.permissionCode === TRACKER_PERMISSION_CODE[area]
      && permission.isEdit);
  }
}
